'use client';

import * as React from 'react';
import { useState, useEffect, useContext } from 'react';
import { ChevronsUpDown, Plus, Loader2, Building } from 'lucide-react';

import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuShortcut, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from '@/components/ui/sidebar';
import { useTenancy, Tenancy } from '@/contexts/TenancyContext';
import { RefreshContext } from '@/contexts/TenancyContextProvider';
import { auth, db, FirebaseFieldValue } from '@/js/external-js/config/main_config';
import { TenancyQuickSwitcher } from './TenancyQuickSwitcher';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"

interface Tenancy {
  name: string;
  logo: string | null;
  plan: string;
  id: string;
  lastAccessed?: string;
}

export function TenancySwitcher({
  tenancies
}: {
  tenancies: Tenancy[];
}) {
  const { isMobile } = useSidebar();
  const { activeTenancy, setActiveTenancy, loading } = useTenancy();
  const { refreshUsers, updateActiveTenancy } = useContext(RefreshContext);
  const [isSwitching, setIsSwitching] = useState(false);
  const [logoErrors, setLogoErrors] = useState<{[key: string]: boolean}>({});

  const [showTenantImpoersonationNotAllowedMessage, setShowTenantImpoersonationNotAllowedMessage] = useState(false);

  // Initialize with first tenancy if not already set
  React.useEffect(() => {
    // Only set if activeTenancy is not set or is a loading placeholder
    if (!activeTenancy || activeTenancy.name === 'Loading...') {
      // Try to restore from cache
      const cached = (() => {
        try {
          const data = localStorage.getItem('active_tenancy_cache_v1');
          return data ? JSON.parse(data) : undefined;
        } catch {
          return undefined;
        }
      })();
      if (
        cached &&
        tenancies.length > 0 &&
        tenancies.find(t => t.id === cached.id)
      ) {
        setActiveTenancy(tenancies.find(t => t.id === cached.id));
      } else if (tenancies.length > 0) {
        setActiveTenancy(tenancies[0]);
      }
    }
  }, [tenancies, activeTenancy, setActiveTenancy]);

  // If tenancies have loaded and the current tenancy is still 'Loading...', forcibly switch to the first tenancy
  React.useEffect(() => {
    if (
      tenancies.length > 0 &&
      activeTenancy &&
      activeTenancy.name === 'Loading...'
    ) {
      setActiveTenancy(tenancies[0]);
    }
  }, [tenancies, activeTenancy, setActiveTenancy]);

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Only respond to Cmd/Ctrl + digit
      if ((event.metaKey || event.ctrlKey) && !event.shiftKey && !event.altKey) {
        // 1-9: tenancy 1-9, 0: tenancy 10
        if (/^\d$/.test(event.key)) {
          event.preventDefault();
          let idx = parseInt(event.key, 10);
          if (event.key === '0') idx = 10; // Cmd+0 = 10th tenancy
          if (idx >= 1 && idx <= tenancies.length) {
            handleTenancySwitch(tenancies[idx - 1]);
          }
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [tenancies]);

  // Utility for updating active tenancy cache
  function saveCachedActiveTenancy(tenancy: Tenancy) {
    try {
      if (tenancy) {
        localStorage.setItem('active_tenancy_cache_v1', JSON.stringify(tenancy));
      }
    } catch (error) {
      console.error('Failed to save tenancy to cache:', error);
    }
  }

  // Function to handle tenancy switching and update Firebase
  const handleTenancySwitch = async (tenancy: Tenancy) => {
    setIsSwitching(true);
    console.log('[handleTenancySwitch]', tenancy);

    // Update lastAccessed timestamp
    const tenancyWithTimestamp = {
      ...tenancy,
      lastAccessed: new Date().toISOString()
    };

    // Save active tenancy to cache for instant reloads
    saveCachedActiveTenancy(tenancyWithTimestamp);

    // Clear the users cache for the new tenancy to force a fresh load
    try {
      const cacheKey = `users_cache_v1_${tenancy.id}`;
      localStorage.removeItem(cacheKey);
    } catch (error) {
      console.error('Error clearing user cache:', error);
    }

    // // Update the active tenancy in context
    // setActiveTenancy(tenancyWithTimestamp);

    // Get the current user
    const user = auth.currentUser;
    if (!user) {
      setIsSwitching(false);
      return;
    }

    // Get the user's primary organization (first in the list)
    const userRef = db.collection('users').doc(user.uid);
    const userDoc = await userRef.get();

    if (!userDoc.exists) {
      setIsSwitching(false);
      return;
    }

    const userData = userDoc.data();
    const primaryOrg = userData?.primaryTenancy || userData?.organisations?.[0];

    // If tenancy has an id property
    if (tenancy.id) {
      try {
        // If switching to primary organization
        if (primaryOrg === tenancy.id) {
          // Update Firebase first - set mspImpersonation to false and ensure organizations array only contains the primary org
          await userRef.update({
            mspImpersonation: false,
            organisations: [primaryOrg], // Reset to only contain the primary organization
            primaryTenancy: primaryOrg // Add or update the primaryTenancy field
          });

          // Add a small delay to ensure the database update completes
          await new Promise((resolve) => setTimeout(resolve, 300));

          // Then update the context and session storage
          sessionStorage.setItem('web-selectedTenancy', 'true');

          // Use both the local context update and direct provider update
          setActiveTenancy(tenancyWithTimestamp);
          updateActiveTenancy(tenancyWithTimestamp); // Direct update to provider

          // Refresh users data for the new tenancy or reload if we are on a .html page
          if (window.location.pathname.endsWith('.html')) {
            window.location.reload();
          } else {
            refreshUsers();
          }

          setIsSwitching(false);
        } else {
          // Check if impersonation is allowed by fetching the tenancy details
          const tenancyRef = db.collection('customers').doc(tenancy.id);
          const tenancyDoc = await tenancyRef.get();

          if (tenancyDoc.exists) {
            const tenancyData = tenancyDoc.data();

            // Get MSP details
            let techMSP = tenancyData?.mspDetails?.techPartner?.msp || tenancyData?.mspDetails?.msp;
            console.log('[TenancySwitcher]', tenancyData?.mspDetails?.techPartner?.impersonationAllowed, tenancyData?.mspDetails?.impersonationAllowed);
            let techImpersonationAllowed = tenancyData?.mspDetails?.techPartner?.impersonationAllowed || tenancyData?.mspDetails?.impersonationAllowed;

            let businessMSP = tenancyData?.mspDetails?.businessPartner?.msp || tenancyData?.mspDetails?.msp;
            let businessImpersonationAllowed = tenancyData?.mspDetails?.businessPartner?.impersonationAllowed || tenancyData?.mspDetails?.impersonationAllowed;

            // Check if impersonation is allowed
            console.log(techMSP, businessMSP, techImpersonationAllowed);
            if ((techMSP === primaryOrg && techImpersonationAllowed) || (businessMSP === primaryOrg && businessImpersonationAllowed)) {
              // Update Firebase first - ensure organizations array only contains primary org and the selected tenancy
              await userRef.update({
                organisations: [primaryOrg, tenancy.id], // Only keep primary org and the selected tenancy
                mspImpersonation: true,
                primaryTenancy: primaryOrg // Add or update the primaryTenancy field
              });

              // Add a small delay to ensure the database update completes
              await new Promise((resolve) => setTimeout(resolve, 300));

              // Then update the context and session storage
              sessionStorage.setItem('web-selectedTenancy', 'true');

              // Add timestamp to the non-primary tenancy for consistency
              const tenancyWithTimestamp = {
                ...tenancy,
                lastAccessed: new Date().toISOString()
              };

              // Always clear the users cache for the new tenancy to force a fresh load
              try {
                const cacheKey = `users_cache_v1_${tenancy.id}`;
                localStorage.removeItem(cacheKey);
              } catch (error) {
                console.error('Error clearing user cache:', error);
              }

              // Use both the local context update and direct provider update
              setActiveTenancy(tenancyWithTimestamp);
              updateActiveTenancy(tenancyWithTimestamp); // Direct update to provider

              // Refresh users data for the new tenancy or reload if we are on a .html page
              if (window.location.pathname.endsWith('.html')) {
                window.location.reload();
              } else {
                refreshUsers();
              }

              setIsSwitching(false);
            } else {
              // Show error or notification that impersonation is not allowed
              console.error('Impersonation not allowed for this tenancy');
              setShowTenantImpoersonationNotAllowedMessage(true);
              setIsSwitching(false);
            }
          }
        }
      } catch (error) {
        console.error('Error switching tenancy:', error);
        setIsSwitching(false);
      }
    }
  };

  // Handle logo load errors
  const handleLogoError = (tenancyId: string) => {
    setLogoErrors(prev => ({
      ...prev,
      [tenancyId]: true
    }));
  };

  if (!activeTenancy) {
    return null;
  } //

  return (
    <>
      <TenancyQuickSwitcher tenancies={tenancies} onSwitch={handleTenancySwitch} />
      <AlertDialog open={showTenantImpoersonationNotAllowedMessage} onOpenChange={setShowTenantImpoersonationNotAllowedMessage}>
        <AlertDialogContent className="bg-white dark:bg-dark">
          <AlertDialogHeader>
            <AlertDialogTitle>Impersonation not allowed</AlertDialogTitle>
            <AlertDialogDescription>
              <p className="mt-4">Acces to this tenancy has been disabled by the tenancy owner.</p>
              <p className="mt-4">If you believe this is an error or need assistance, please contact the owner directly for support or to request access.</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction>Close</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <SidebarMenu>
        <SidebarMenuItem>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <SidebarMenuButton size="lg" className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground">
                <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-transparent text-sidebar-primary-foreground">
                  {loading || isSwitching ? (
                    <Loader2 className="size-4 animate-spin text-white" />
                  ) : logoErrors[activeTenancy.id || 'active'] || !activeTenancy.logo ? (
                    <div className="size-8 flex items-center justify-center font-bold text-white">
                      {activeTenancy.name.charAt(0).toUpperCase()}
                    </div>
                  ) : (
                    <img
                      src={activeTenancy.logo}
                      alt={activeTenancy.name}
                      className="w-14 h-14 object-contain overflow-hidden"
                      onError={() => handleLogoError(activeTenancy.id || 'active')}
                    />
                  )}
                </div>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold text-white">{activeTenancy.name}</span>
                  <span className="truncate text-xs text-gray-300">{activeTenancy.plan}</span>
                </div>
                <ChevronsUpDown className="ml-auto" />
              </SidebarMenuButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg" align="start" side={isMobile ? 'bottom' : 'right'} sideOffset={4}>
              <DropdownMenuLabel className="text-xs text-muted-foreground">{loading ? 'Loading tenancies...' : 'Tenancies'}</DropdownMenuLabel>
              {tenancies.map((team, index) => (
                <DropdownMenuItem key={team.name} onClick={() => handleTenancySwitch(team)} className="gap-2 p-2" disabled={loading || isSwitching}>
                  <div className="flex size-6 items-center justify-center rounded-sm border">
                    {logoErrors[team.id || team.name] || !team.logo ? (
                      <div className="size-4 flex items-center justify-center font-bold">
                        {team.name.charAt(0).toUpperCase()}
                      </div>
                    ) : (
                      <img
                        src={team.logo}
                        alt={team.name}
                        className="size-6 shrink-0 object-contain"
                        onError={() => handleLogoError(team.id || team.name)}
                      />
                    )}
                  </div>
                  {team.name}
                  <DropdownMenuShortcut>⌘{index + 1}</DropdownMenuShortcut>
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem className="gap-2 p-2">
                <div className="flex size-6 items-center justify-center rounded-md border bg-background">
                  <Plus className="size-4" />
                </div>
                <div className="font-medium text-muted-foreground">Add tenancy</div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarMenuItem>
      </SidebarMenu>
    </>
  );
}
